/**
 * utils/idGenerators.js
 * Pure helpers: ID generation, date/number formatting. No state, no UI.
 */

const freshId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Set only while withIdRecording/withIdReplay is running a change (multi-device sync).
let idHook = null;

/** Unique-enough id: timestamp + random suffix. The ONE id source in this app. */
export const generateId = () => (idHook ? idHook() : freshId());

/**
 * Run `fn` and record every id `generateId` hands out during it. Used by the shared-books sync
 * (utils/remoteSync.js) when a change is first made, so that if the change later has to be
 * re-applied on top of someone else's newer data, it can get the SAME ids back — a record the
 * screen already holds by id (a just-created contact or salesperson role picked in a form)
 * then still exists under that id. Synchronous only; never nests.
 */
export function withIdRecording(fn) {
  const ids = [];
  const prev = idHook;
  idHook = () => { const id = freshId(); ids.push(id); return id; };
  try {
    return { result: fn(), ids };
  } finally {
    idHook = prev;
  }
}

/** Run `fn` handing out `ids` in order (then fresh ones if it asks for more). See above. */
export function withIdReplay(ids, fn) {
  let i = 0;
  const prev = idHook;
  idHook = () => (i < ids.length ? ids[i++] : freshId());
  try {
    return fn();
  } finally {
    idHook = prev;
  }
}

/** Today's date as YYYY-MM-DD in the user's LOCAL timezone (matches the wall clock). */
export const today = () => {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
};

/** Current time as HH:MM (local). */
export const nowTime = () => new Date().toTimeString().slice(0, 5);

/**
 * Combine a local "YYYY-MM-DD" date string and "HH:MM" time string into a single ISO
 * timestamp — the shape `payments[].paidAt` needs (unlike a transaction's own `date`/`time`,
 * which stay two separate fields forever). Built from a real local `Date` so it matches the
 * wall-clock semantics `today()`/`nowTime()` already use, then serialized the same way
 * `at`/`createdAt` already are. A blank/invalid date or time falls back to `today()`/
 * `nowTime()` respectively — this must never produce a blank/NaN result, since nothing in
 * this app gates submission on it being non-empty.
 */
export const combineDateTime = (dateStr, timeStr) => {
  const [y, mo, da] = (dateStr || today()).split("-").map(Number);
  const [hh, mm] = (timeStr || nowTime()).split(":").map(Number);
  const d = new Date(y, (mo || 1) - 1, da || 1, hh || 0, mm || 0, 0, 0);
  if (Number.isNaN(d.getTime())) return new Date().toISOString();
  return d.toISOString();
};

/**
 * Inverse of combineDateTime — splits an ISO timestamp back into local "YYYY-MM-DD"/"HH:MM"
 * strings, for pre-filling a paidAt date+time input pair from a stored value. Falls back to
 * `{date: today(), time: nowTime()}` on invalid/empty input, same never-blank contract as
 * combineDateTime.
 */
export const splitDateTime = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return { date: today(), time: nowTime() };
  return {
    date: [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-"),
    time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
  };
};

/**
 * Format a quantity for display (Indonesian locale, "." thousands separator).
 * 50000 -> "50.000", 0 -> "0". Returns "0" for null/empty/NaN.
 */
export const fmtQty = (n) => {
  if (n === null || n === undefined || n === "") return "0";
  const num = Number(n);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("id-ID");
};

/** Render a signed delta with explicit sign: 5 -> "+5", -2 -> "−2", 0 -> "0". */
export const fmtDelta = (n) => {
  const num = Number(n) || 0;
  if (num > 0) return `+${fmtQty(num)}`;
  if (num < 0) return `−${fmtQty(Math.abs(num))}`; // U+2212 minus sign
  return "0";
};

/**
 * Format an ISO timestamp (or YYYY-MM-DD) to a readable Indonesian date+time.
 * Falls back to "-" on invalid input.
 */
export const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MONTH_SHORT_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const MONTH_LONG_ID = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

/**
 * Shared date-string parser for fmtDateShort/fmtDateLong below — built from the STRING's own
 * digits, never a `Date` object: a bare "YYYY-MM-DD" parses as UTC midnight, and converting
 * THAT to a local calendar date for display can shift the day by ±1 depending on the
 * runtime's timezone; string-parsing can't drift. Returns null on invalid/empty input.
 */
const parseDateStrParts = (dateStr) => {
  if (!dateStr) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!match) return null;
  const [, y, m, d] = match;
  const monthIdx = Number(m) - 1;
  if (monthIdx < 0 || monthIdx > 11) return null;
  return { y, d: Number(d), monthIdx };
};

/**
 * Format a "YYYY-MM-DD" (or a full ISO timestamp — only the leading date part is read) as a
 * friendly Indonesian day+month+year, e.g. "24 Jul 2026". Falls back to "-" on invalid input.
 */
export const fmtDateShort = (dateStr) => {
  const parts = parseDateStrParts(dateStr);
  if (!parts) return "-";
  return `${parts.d} ${MONTH_SHORT_ID[parts.monthIdx]} ${parts.y}`;
};

/**
 * Same contract as fmtDateShort, but with the FULL Indonesian month name, e.g. "28 Juli 2026"
 * — used where a printed document calls for a formal, unabbreviated date (Phase T2c-1-fix,
 * Invoice Pusat's header). Shares the same string-parsing core as fmtDateShort rather than a
 * second independent date formatter, so the two can never disagree on which digits mean what.
 */
export const fmtDateLong = (dateStr) => {
  const parts = parseDateStrParts(dateStr);
  if (!parts) return "-";
  return `${parts.d} ${MONTH_LONG_ID[parts.monthIdx]} ${parts.y}`;
};

/**
 * Same contract as fmtDateShort/fmtDateLong, but "DD-Mon-YY" — zero-padded day, the 3-letter
 * month, zero-padded 2-digit year, e.g. "28-Jul-26" — used by Invoice Cabang's own date line
 * (Phase T2d, PRD §5.3, distinct from the other documents' own date conventions). Shares the
 * same string-parsing core as fmtDateShort/fmtDateLong.
 */
export const fmtDateDMY = (dateStr) => {
  const parts = parseDateStrParts(dateStr);
  if (!parts) return "-";
  const dd = String(parts.d).padStart(2, "0");
  const yy = parts.y.slice(-2);
  return `${dd}-${MONTH_SHORT_ID[parts.monthIdx]}-${yy}`;
};

/**
 * Item-code-reuse fix: scans every place an internalCode has EVER appeared — items,
 * transaction lines, history entries (itemCode field), recipe inputs/outputs, transformation
 * inputs/outputs — in ONE pass per source array, regardless of how many distinct prefixes are
 * present. Returns Map<prefix, highestNumberEverUsed>. Deleting the highest-numbered item in a
 * category used to free its number for reuse the instant it left `items`, even while a
 * transaction/history/recipe/transformation still referenced it by that exact code — this is
 * the single source of truth every minting path (nextInternalCode below, and
 * importCommit.js's own bulk-import counter) now reads from.
 *
 * Category codes are enforced to be `[A-Z0-9]+`, max 5 chars, by validateCategoryCode at every
 * real code-creation path (resolveItemCreate's explicitCode branch, KelolaKategoriModal's own
 * add/edit gate, and importCommit.js's validatePrefixPlan) — so this regex's character class
 * matches every prefix this app can actually produce; it does not need to accept arbitrary
 * characters the way the old per-prefix regex (built from the prefix string itself) did.
 */
export function maxCodeNumbersByPrefix(data) {
  const maxByPrefix = new Map();
  const codeRe = /^([A-Z0-9]+)-(\d+)$/;
  const consider = (code) => {
    const m = codeRe.exec(code || "");
    if (!m) return;
    const num = parseInt(m[2], 10);
    if (Number.isNaN(num)) return;
    if (num > (maxByPrefix.get(m[1]) || 0)) maxByPrefix.set(m[1], num);
  };
  for (const it of data.items || []) consider(it.internalCode);
  for (const t of data.transactions || []) {
    for (const l of t.lines || []) consider(l.internalCode);
  }
  for (const h of data.history || []) consider(h.itemCode);
  for (const r of data.recipes || []) {
    for (const l of r.inputs || []) consider(l.internalCode);
    for (const l of r.outputs || []) consider(l.internalCode);
  }
  for (const tf of data.transformations || []) {
    for (const l of tf.inputs || []) consider(l.internalCode);
    for (const l of tf.outputs || []) consider(l.internalCode);
  }
  return maxByPrefix;
}

/**
 * Generate the next sequential internal code (temporary placeholder convention, per plan B1).
 * Takes the FULL data object (not a bare items array) — see maxCodeNumbersByPrefix above for
 * why: a code retired by item deletion must never be reissued while it still appears in a
 * transaction, history entry, recipe, or transformation. Internal code remains the primary key
 * regardless of how it is generated. Default prefix "IJM" keeps existing callers unaffected.
 *
 * One full scan per call — fine for a single-item mint (this function's only real use, since
 * bulk minting has its own O(1)-per-item counter seeded from ONE upfront
 * maxCodeNumbersByPrefix call; see importCommit.js's buildCommit and App.js's
 * commitPendingItems, neither of which calls this function inside a loop).
 */
export const nextInternalCode = (data, prefix = "IJM") => {
  const max = maxCodeNumbersByPrefix(data || {}).get(prefix) || 0;
  return `${prefix}-${String(max + 1).padStart(5, "0")}`;
};
